/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useContext, useMemo, useState } from 'react';

import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { InputAdornment } from '@material-ui/core';

import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'lodash';
import Tooltip from '../../common/Tooltip';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import { sortCollection } from '../../../shared/utils/objectUtils';
import { SubmissionContext } from '../context/SubmissionContextProvider';
import useUniversityIntegration from './useUniversityIntegration';
import { mapEntityFields } from '../../../shared/submission/entityFieldsMapper';
import { selectVersion } from '../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../shared/utils/versionUtils';

const asNumber = val => val;
const guessType = val => val;

const nums = new Set(['number', 'integer']);

const useStyles = makeStyles(theme => ({
  root: () => ({
    '& label': {
      transform: 'translate(0.875rem, 1rem) scale(1)',
      color: theme.palette.input,
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  }),
  inputAdornment: {
    position: 'absolute',
    right: '-3rem',
  },
  defaultMenuItem: {
    color: theme.palette.input,
  },
}));

/**
 * This is a silly limitation in the DOM where option change event values are
 * always retrieved as strings.
 */
const processValue = (schema, value) => {
  // "enum" is a reserved word, so only "type" and "items" can be destructured
  const { type, items } = schema;
  if (value === '') {
    return undefined;
  }
  if (type === 'array' && items && nums.has(items.type)) {
    return value.map(asNumber);
  }
  if (type === 'boolean') {
    return value === 'true';
  }
  if (type === 'number') {
    return asNumber(value);
  }

  // If type is undefined, but an enum is present, try and infer the type from
  // the enum values
  if (schema.enum) {
    if (schema.enum.every(x => guessType(x) === 'number')) {
      return asNumber(value);
    }
    if (schema.enum.every(x => guessType(x) === 'boolean')) {
      return value === 'true';
    }
  }

  return value;
};

const SelectWidget = ({
  schema,
  uiSchema,
  formContext,
  registry,
  options,
  label,
  required,
  disabled,
  readonly,
  value,
  multiple,
  autofocus,
  id,
  onChange,
  onBlur,
  onFocus,
  rawErrors = [],
}) => {
  const { i18n, t } = useTranslation();
  const { fieldsToClear, addFieldToClear, removeFieldToClear } = useContext(SubmissionContext);
  const { enumOptions, enumDisabled } = options;
  const emptyValue = multiple ? [] : '';
  const enumOptionsMapper = uiSchema?.config?.enumOptionsMapper;
  const selectedVersion = useSelector(selectVersion);

  const enumOptionsSorted = useMemo(() => sortCollection(enumOptions, schema?.config?.orderBy, i18n?.language), [enumOptions, schema?.config?.orderBy, i18n?.language]);
  const displayLabel = (uiSchema?.config?.title && t(uiSchema?.config?.title)) || label || schema.title;
  const { callIntegrationFn, widgetIntegrationService, integrationEnumOptions } = useUniversityIntegration(
    uiSchema,
    id,
    registry,
    formContext,
  );

  const enumOptionsMapperResult = _.get(formContext, enumOptionsMapper?.sourcePath)
    ?.map((item) => {
      const { entries } = mapEntityFields(enumOptionsMapper?.mapping, item, schema);
      if (entries && Array.isArray(entries) && entries.length > 0) return entries[0];
      // for broken / not matching mapping
      return undefined;
    })
    .filter(item => item !== undefined);

  const selectOptions = enumOptionsMapperResult || integrationEnumOptions || enumOptionsSorted || [];
  const _onChange = ({ target: { value: valueLocal } }) => {
    if (uiSchema?.['ui:options']?.triggerPostProcess === true || schema?.config?.triggerPostProcess === true) {
      registry.triggerPostProcess(id, valueLocal);
    }
    onChange(processValue(schema, valueLocal));
    // configured to reset other fields on change

    const resetsFieldIds = schema?.config?.resetsFieldIds;
    if (resetsFieldIds) {
      resetsFieldIds.forEach((resetsFieldId) => {
        const prefix = id.substring(0, id.lastIndexOf('_'));
        const fullId = `${prefix}_${resetsFieldId}`;
        setTimeout(() => addFieldToClear(fullId), [100]);
      });
    }

    if (widgetIntegrationService) {
      callIntegrationFn(valueLocal);
    }
  };

  // clears the content of the field if it was marked by other form field
  useEffect(() => {
    if (fieldsToClear.includes(id)) {
      onChange(undefined);
      removeFieldToClear(id);
    }
  }, [JSON.stringify(fieldsToClear)]);

  const _onBlur = ({ target: { value: valueLocal } }) => onBlur(id, processValue(schema, valueLocal));
  const _onFocus = ({ target: { value: valueLocal } }) => onFocus(id, processValue(schema, valueLocal));
  const classes = useStyles();
  const tooltip = uiSchema?.[uiSchemaProperties.UI_TOOLTIP];

  useEffect(() => {
    if (schema?.config?.type === 'clear') {
      onChange(options.emptyValue);
    }
  }, []);

  const testId = `${schema?.title}-dropdown` || 'dropdown';

  const valueLabel = Array.isArray(selectOptions)
    ? selectOptions?.find(selectOption => selectOption?.value === value)?.label
    : undefined;

  const [showTooltip, setShowTooltip] = useState(false);
  const labelElem = document.getElementById(`${id}-label`);

  const onMouseEnterCallback = () => {
    const isOverflowing = labelElem?.offsetWidth < labelElem?.scrollWidth;
    if (isOverflowing) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  const onMouseExitCallback = () => {
    setShowTooltip(false);
  };

  return (
    <Tooltip placement="top" open={showTooltip} title={displayLabel}>
      <TextField
        id={id}
        onMouseEnter={onMouseEnterCallback}
        onMouseLeave={onMouseExitCallback}
        className={`${classes.root}`}
        {...dataTestId({ value: `${testId}--${selectedVersion.value}` })}
        label={displayLabel}
        tabIndex={-1}
        select
        value={typeof value === 'undefined' ? emptyValue : value}
        size="small"
        required={required}
        disabled={disabled || readonly}
        autoFocus={autofocus}
        error={rawErrors.length > 0}
        onChange={_onChange}
        onBlur={_onBlur}
        onFocus={_onFocus}
        InputProps={{
          endAdornment: tooltip && (
            <InputAdornment position="end" className={classes.inputAdornment}>
              <Tooltip title={<Trans i18nKey={tooltip} />} />
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: !!value || (!(disabled || readonly) && !value),
        }}
        SelectProps={{
          multiple: typeof multiple === 'undefined' ? false : multiple,
          MenuProps: {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            PaperProps: {
              style: {
                maxHeight: '20rem',
              },
            },
          },
          inputProps: {
            'value-label': valueLabel,
          },
        }}
      >
        <MenuItem className={classes.defaultMenuItem} key={`select-${id}`} value="" disabled={false}>
          --
          {t('select.option')}
          --
        </MenuItem>
        {selectOptions &&
          selectOptions.map(({ value: valueLocal, label: labelLocal }, i) => {
            const isDisabled = enumDisabled && enumDisabled.indexOf(valueLocal) !== -1;
            return (
              <MenuItem key={i} value={valueLocal} disabled={isDisabled}>
                {labelLocal}
              </MenuItem>
            );
          })}
      </TextField>
    </Tooltip>
  );
};

export default SelectWidget;
